import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  BUNDLED_LOCALE,
  applyMessages,
  configuredLocale,
  currentLocale,
  desiredLocale,
  isLocaleCode,
  setLocale,
} from '@/i18n'
import {
  readCachedLanguages,
  readCachedMessages,
  writeCachedLanguages,
  writeCachedMessages,
} from '@/i18n/messageCache'
import { getLanguages, getTranslations } from '@/services/endpoints/translations'
import type { Language } from '@/types/translation'

/**
 * Lingue disponibili e messaggi della lingua attiva.
 *
 * Da quando le traduzioni stanno a database, questo store è ciò che rende
 * l'interfaccia leggibile: nessuna view può disegnarsi in modo sensato prima che
 * abbia fatto il suo lavoro, ed è per questo che `bootstrapSession` lo attende.
 */

/**
 * Unica lingua nominata nel codice, e solo per il caso degenere: primissima
 * visita con l'API irraggiungibile e nessuna cache. Appena una risposta arriva
 * (o esiste una copia in localStorage) l'elenco viene dal database.
 */
const MINIMAL_ENGLISH: Language = {
  id: 0,
  iso_code: BUNDLED_LOCALE,
  description: 'English',
  flag_url: null,
  active: true,
  order: 0,
  created_at: null,
  updated_at: null,
}

export const useLocalesStore = defineStore('locales', () => {
  const languages = ref<Language[]>([])
  /** Vero quando l'elenco lingue è stato richiesto, con successo o meno. */
  const loaded = ref(false)

  /** Locali i cui messaggi sono già stati passati a vue-i18n. */
  const applied = new Set<string>([BUNDLED_LOCALE])
  /** Versione dei messaggi applicati, per lingua: evita ri-render inutili. */
  const versions = new Map<string, string | null>()

  let readyPromise: Promise<void> | null = null

  /** Le sole lingue proponibili all'utente. */
  const selectable = computed<Language[]>(() => {
    const active = languages.value.filter((language) => language.active)
    return active.length > 0 ? active : [MINIMAL_ENGLISH]
  })

  function languageFor(iso: string): Language | undefined {
    return selectable.value.find((language) => language.iso_code === iso)
  }

  function isKnown(iso: string): boolean {
    return selectable.value.some((language) => language.iso_code === iso)
  }

  /**
   * Lingua effettivamente servibile fra quelle note, la più vicina a `requested`.
   *
   * Finché l'elenco non è stato caricato si tenta comunque quella richiesta: al
   * primo avvio non c'è nulla contro cui validare, e rifiutarla significherebbe
   * mostrare l'inglese a chi ha già scelto altro.
   */
  function servable(requested: string): string {
    if (!isLocaleCode(requested)) return BUNDLED_LOCALE
    if (!loaded.value) return requested
    if (isKnown(requested)) return requested

    // La lingua è stata disattivata o cancellata dal pannello mentre era la
    // scelta di qualcuno: si ripiega sul default, non si lascia l'app muta.
    const configured = configuredLocale()
    return isKnown(configured) ? configured : BUNDLED_LOCALE
  }

  /** Scarica e applica i messaggi di una lingua. Solleva se la chiamata fallisce. */
  async function loadMessages(iso: string, { silent = true }: { silent?: boolean } = {}) {
    const response = await getTranslations(iso, { silent })

    applyMessages(iso, response.translations)
    applied.add(iso)
    versions.set(iso, response.version)
    writeCachedMessages(iso, { version: response.version, messages: response.translations })
  }

  /**
   * Ricarica i messaggi della lingua attiva se a database sono cambiati.
   *
   * Gira in background dopo l'avvio: la copia in cache rende l'app immediata, e
   * questo la riallinea senza che l'utente aspetti. Se la versione combacia non
   * si tocca nulla, così non si scatena un ri-render per niente.
   */
  async function revalidate(iso: string): Promise<void> {
    try {
      const response = await getTranslations(iso, { silent: true })
      if (response.version !== null && response.version === versions.get(iso)) return

      applyMessages(iso, response.translations)
      applied.add(iso)
      versions.set(iso, response.version)
      writeCachedMessages(iso, { version: response.version, messages: response.translations })
    } catch {
      // Si resta con i messaggi che si hanno: sono comunque leggibili.
    }
  }

  /**
   * Idratazione **sincrona** dalla cache, da chiamare prima del mount.
   *
   * È ciò che evita il lampo di inglese a chi ha già visitato il sito: i messaggi
   * salvati sono disponibili prima del primo render, senza attendere la rete.
   */
  function hydrate(): void {
    const cached = readCachedLanguages()
    if (cached) languages.value = cached

    const wanted = desiredLocale()
    if (wanted === currentLocale()) return

    const messages = readCachedMessages(wanted)
    if (!messages) return

    applyMessages(wanted, messages.messages)
    applied.add(wanted)
    versions.set(wanted, messages.version)
    setLocale(wanted, { persist: false })
  }

  /**
   * Porta l'app sulla lingua richiesta, caricandone i messaggi se non li ha.
   *
   * @param persist salva la scelta per la prossima visita: vero quando è l'utente
   *                a scegliere, falso quando il valore arriva dal suo account.
   */
  async function use(requested: string, { persist = true }: { persist?: boolean } = {}) {
    const target = servable(requested)

    if (!applied.has(target)) {
      await loadMessages(target, { silent: false })
    }

    // Nessuna scorciatoia quando il locale è già quello attivo: `setLocale` deve
    // comunque poter salvare la scelta. Altrimenti una chiamata precedente con
    // `persist: false` (quella che applica la lingua dell'account) impedirebbe a
    // quella dell'utente di essere ricordata.
    setLocale(target, { persist })
  }

  /** Carica l'elenco lingue una volta per sessione (chiamate concorrenti la condividono). */
  function ensureReady(): Promise<void> {
    readyPromise ??= load()
    return readyPromise
  }

  async function load(): Promise<void> {
    try {
      const fetched = await getLanguages()
      if (fetched.length > 0) {
        languages.value = fetched
        writeCachedLanguages(fetched)
      }
    } catch {
      // Si resta con l'elenco in cache, o con il solo inglese del bundle.
    } finally {
      loaded.value = true
    }

    const target = servable(desiredLocale())

    if (!applied.has(target)) {
      try {
        // Bloccante: senza i suoi messaggi l'app si disegnerebbe in inglese.
        await loadMessages(target)
      } catch {
        // L'inglese del bundle resta come rete di sicurezza.
        return
      }
    }

    // `persist: false`: il locale viene da localStorage o dalla configurazione,
    // non è una scelta appena fatta dall'utente.
    setLocale(target, { persist: false })
    void revalidate(target)
  }

  /**
   * Rilegge lingue e messaggi dopo una modifica dal pannello.
   *
   * Serve perché chi modifica una label sta guardando l'interfaccia che quella
   * label disegna: senza, vedrebbe il valore vecchio fino al reload.
   *
   * ⚠️ Ricarica **la lingua attiva**, non quella che risolverebbe il boot. Passando
   * da `ensureReady()` il locale tornerebbe alla preferenza salvata nel browser, e
   * un admin con una lingua diversa sull'account si vedeva l'interfaccia cambiare
   * lingua a ogni salvataggio.
   */
  async function reload(): Promise<void> {
    const active = currentLocale()

    try {
      const fetched = await getLanguages()
      if (fetched.length > 0) {
        languages.value = fetched
        writeCachedLanguages(fetched)
      }
    } catch {
      // Si tiene l'elenco che si ha già.
    }

    applied.delete(active)
    versions.delete(active)

    // La lingua attiva può essere stata appena disattivata o cancellata da chi sta
    // usando il pannello: in quel caso si ripiega come al boot.
    const target = servable(active)

    try {
      await loadMessages(target)
    } catch {
      // Si resta con i messaggi correnti: sono comunque leggibili.
    }

    setLocale(target, { persist: false })
  }

  return {
    languages,
    loaded,
    selectable,
    languageFor,
    isKnown,
    hydrate,
    ensureReady,
    use,
    reload,
  }
})
